// Serialisierung/Deserialisierung des EINGABE-Zustands für teilbare/
// speicherbare URLs (Issue #43).
//
// WICHTIG: Hier werden ausschließlich die Eingabeparameter kodiert/dekodiert.
// Es findet KEINE fachliche Berechnung statt – die geschützten Berechnungs-
// funktionen (Preis, Druckverlust, LwA, Gewicht, Einfügungsdämpfung, CSV)
// werden nicht berührt. Reine, gut testbare Funktionen.
//
// Datenschutz: Es werden nur technische Konfigurationsparameter abgebildet,
// keine personenbezogenen Daten (insbesondere kein freier PDF-Dateiname).

export const KULISSENTYP_VALUES = ['HKS', 'HKR', 'HKL', 'HKRL'];
export const KULISSENBREITE_VALUES = [100, 200, 230, 300];
export const DRUCKBEREICH_VALUES = ['ND', 'MD', 'HD'];
export const ANSCHLUSSPROFIL_VALUES = ['SBM20', 'SBM30', 'SBM40', 'WER30', 'WER40'];

// Defaults müssen exakt den Initialwerten der Eingabe-Komponenten entsprechen,
// damit ohne URL-Parameter das bisherige Verhalten unverändert bleibt.
export const SCHALLDAEMPFER_DEFAULTS = {
  kanalbreite: 1000,
  kanalhoehe: 1000,
  kanallaenge: 1500,
  volumenstrom: 1000,
  selectedKulissentyp: 'HKS',
  selectedKulissenbreite: 100,
  selectedKulissenanzahl: 3,
  selectedDruckbereich: 'ND',
  selectedAnschlussprofil: 'SBM20',
  checkedAnschlussprofil: false,
  rabatt: 0,
  optionenChecked: [false, false, false],
};

export const KULISSEN_DEFAULTS = {
  kulissenhoehe: 1000,
  kulissenlaenge: 1000,
  selectedKulissenbreite: 100,
  selectedKulissentyp: 'HKS',
  rabatt: 0,
  optionenChecked: [false, false, false],
};

function parseIntInRange(raw, { min, max, fallback }) {
  const value = Number.parseInt(raw, 10);
  if (!Number.isFinite(value) || value < min || value > max) {
    return fallback;
  }
  return value;
}

function parseFloatInRange(raw, { min, max, fallback }) {
  const value = Number.parseFloat(raw);
  if (!Number.isFinite(value) || value < min || value > max) {
    return fallback;
  }
  return value;
}

function parseEnumValue(raw, allowed, fallback) {
  return allowed.includes(raw) ? raw : fallback;
}

function parseNumberEnumValue(raw, allowed, fallback) {
  const value = Number(raw);
  return allowed.includes(value) ? value : fallback;
}

function parseBoolValue(raw, fallback) {
  if (raw === '1' || raw === 'true') {
    return true;
  }
  if (raw === '0' || raw === 'false') {
    return false;
  }
  return fallback;
}

function toBool3(optionenChecked) {
  const arr = Array.isArray(optionenChecked) ? optionenChecked : [];
  return [Boolean(arr[0]), Boolean(arr[1]), Boolean(arr[2])];
}

// --- Schalldämpfer (Eingabe.jsx) -----------------------------------------

export function serializeSchalldaempferConfig(config) {
  const params = new URLSearchParams();
  params.set('kb', String(config.kanalbreite));
  params.set('kh', String(config.kanalhoehe));
  params.set('kl', String(config.kanallaenge));
  params.set('vs', String(config.volumenstrom));
  params.set('kt', String(config.selectedKulissentyp));
  params.set('kbr', String(config.selectedKulissenbreite));
  params.set('ka', String(config.selectedKulissenanzahl));
  params.set('db', String(config.selectedDruckbereich));
  params.set('ap', String(config.selectedAnschlussprofil));
  params.set('apc', config.checkedAnschlussprofil ? '1' : '0');
  params.set('r', String(config.rabatt));
  const optionen = toBool3(config.optionenChecked);
  params.set('o1', optionen[0] ? '1' : '0');
  params.set('o2', optionen[1] ? '1' : '0');
  params.set('o3', optionen[2] ? '1' : '0');
  return params.toString();
}

export function parseSchalldaempferConfig(search) {
  const params = new URLSearchParams(search || '');
  const d = SCHALLDAEMPFER_DEFAULTS;
  return {
    kanalbreite: parseIntInRange(params.get('kb'), { min: 200, max: 4000, fallback: d.kanalbreite }),
    kanalhoehe: parseIntInRange(params.get('kh'), { min: 200, max: 4000, fallback: d.kanalhoehe }),
    kanallaenge: parseIntInRange(params.get('kl'), { min: 200, max: 4000, fallback: d.kanallaenge }),
    volumenstrom: parseIntInRange(params.get('vs'), { min: 0, max: 1000000, fallback: d.volumenstrom }),
    selectedKulissentyp: parseEnumValue(params.get('kt'), KULISSENTYP_VALUES, d.selectedKulissentyp),
    selectedKulissenbreite: parseNumberEnumValue(params.get('kbr'), KULISSENBREITE_VALUES, d.selectedKulissenbreite),
    selectedKulissenanzahl: parseIntInRange(params.get('ka'), { min: 1, max: 100, fallback: d.selectedKulissenanzahl }),
    selectedDruckbereich: parseEnumValue(params.get('db'), DRUCKBEREICH_VALUES, d.selectedDruckbereich),
    selectedAnschlussprofil: parseEnumValue(params.get('ap'), ANSCHLUSSPROFIL_VALUES, d.selectedAnschlussprofil),
    checkedAnschlussprofil: parseBoolValue(params.get('apc'), d.checkedAnschlussprofil),
    rabatt: parseFloatInRange(params.get('r'), { min: 0, max: 100, fallback: d.rabatt }),
    optionenChecked: [
      parseBoolValue(params.get('o1'), false),
      parseBoolValue(params.get('o2'), false),
      parseBoolValue(params.get('o3'), false),
    ],
  };
}

// --- Kulissen (EingabeKulissenwerte.jsx) ---------------------------------

export function serializeKulissenConfig(config) {
  const params = new URLSearchParams();
  params.set('kh', String(config.kulissenhoehe));
  params.set('kl', String(config.kulissenlaenge));
  params.set('kbr', String(config.selectedKulissenbreite));
  params.set('kt', String(config.selectedKulissentyp));
  params.set('r', String(config.rabatt));
  const optionen = toBool3(config.optionenChecked);
  params.set('o1', optionen[0] ? '1' : '0');
  params.set('o2', optionen[1] ? '1' : '0');
  params.set('o3', optionen[2] ? '1' : '0');
  return params.toString();
}

export function parseKulissenConfig(search) {
  const params = new URLSearchParams(search || '');
  const d = KULISSEN_DEFAULTS;
  return {
    kulissenhoehe: parseIntInRange(params.get('kh'), { min: 200, max: 4000, fallback: d.kulissenhoehe }),
    kulissenlaenge: parseIntInRange(params.get('kl'), { min: 200, max: 4000, fallback: d.kulissenlaenge }),
    selectedKulissenbreite: parseNumberEnumValue(params.get('kbr'), KULISSENBREITE_VALUES, d.selectedKulissenbreite),
    selectedKulissentyp: parseEnumValue(params.get('kt'), KULISSENTYP_VALUES, d.selectedKulissentyp),
    rabatt: parseFloatInRange(params.get('r'), { min: 0, max: 100, fallback: d.rabatt }),
    optionenChecked: [
      parseBoolValue(params.get('o1'), false),
      parseBoolValue(params.get('o2'), false),
      parseBoolValue(params.get('o3'), false),
    ],
  };
}

// Baut eine vollständige, teilbare URL aus einem Query-String – ohne
// hartcodierte Domain (funktioniert lokal, in Codespaces und auf IONOS).
export function buildShareUrl(queryString) {
  if (typeof window === 'undefined' || !window.location) {
    return `?${queryString}`;
  }
  const { origin, pathname } = window.location;
  return `${origin}${pathname}?${queryString}`;
}
